import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'April 23, 2026';
const CONTACT_EMAIL = 'legal@glowlytix.app';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1c1917', marginBottom: '0.75rem', fontFamily: 'Georgia, serif' }}>
        {title}
      </h2>
      <div style={{ color: '#57534e', lineHeight: 1.7, fontSize: '0.9375rem' }}>{children}</div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#fafaf9', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e7e5e4', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Link to="/" style={{ color: '#a8a29e', textDecoration: 'none', fontSize: '0.875rem' }}>← Back</Link>
        <span style={{ color: '#e7e5e4' }}>|</span>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.125rem', color: '#1c1917', fontWeight: 600 }}>Glowlytix</span>
      </div>

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2.5rem 1.5rem 4rem' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '2rem', fontWeight: 700, color: '#1c1917', marginBottom: '0.5rem' }}>
          Terms of Service
        </h1>
        <p style={{ color: '#a8a29e', fontSize: '0.875rem', marginBottom: '2.5rem' }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '2.5rem' }}>
          <p style={{ color: '#92400e', fontSize: '0.875rem', margin: 0, lineHeight: 1.6 }}>
            <strong>Not a medical device.</strong> Glowlytix is a personal wellness and self-tracking tool. It does not provide medical advice, diagnosis, or treatment recommendations. Always consult a licensed dermatologist or healthcare professional for any skin health concern.
          </p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            By creating an account or using the Glowlytix application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you may not use the Service. These Terms apply to all users including free-tier and paid subscribers.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            Glowlytix is a personal skin-tracking application that allows users to log daily skin entries (including photos, scores, routines, and notes), generate AI-powered insights, and track progress over time. The Service is provided as a web application accessible at glowlytix.app.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.
          </p>
        </Section>

        <Section title="3. Eligibility">
          <p>
            You must be at least 13 years old to use Glowlytix. By using the Service, you represent that you meet this age requirement. Users between 13 and 18 must have consent from a parent or legal guardian.
          </p>
        </Section>

        <Section title="4. Accounts">
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to:
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Provide accurate and complete information when creating your account.</li>
            <li style={{ marginBottom: '0.5rem' }}>Use a strong, unique password of at least 8 characters.</li>
            <li style={{ marginBottom: '0.5rem' }}>Notify us immediately of any unauthorized use of your account.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            We reserve the right to suspend or terminate accounts that violate these Terms.
          </p>
        </Section>

        <Section title="5. Subscriptions and Billing">
          <p>
            Glowlytix offers a free tier and paid subscription plans ("Glow" and "Radiant Pro"). Paid plans are billed annually via Stripe, our third-party payment processor. By subscribing, you authorize us to charge your payment method on a recurring basis.
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>Trial period:</strong> New accounts receive a 14-day free trial of Radiant Pro. No charge is made until a payment method is added and the trial period ends.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Cancellation:</strong> You may cancel your subscription at any time from Settings. Access continues through the end of your current billing period; no refunds are issued for partial periods.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>Price changes:</strong> We will provide at least 30 days' advance notice of any price changes before they take effect on your renewal date.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            All prices are in USD. Glowlytix, Inc. is responsible for subscription billing; Apple App Store or Google Play in-app purchase billing terms apply where purchases are made through those platforms.
          </p>
        </Section>

        <Section title="6. Not Medical Advice">
          <p>
            The Glowlytix Service is for informational and self-tracking purposes only. Nothing in the Service — including AI-generated skin insights, trend analysis, timelapse features, or routine recommendations — constitutes medical advice, diagnosis, or treatment.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            You acknowledge that: (a) skin conditions may require professional evaluation; (b) AI insights are generated from your self-reported data and may be incomplete or inaccurate; (c) you should always consult a licensed dermatologist or physician for any medical concern.
          </p>
        </Section>

        <Section title="7. Your Content">
          <p>
            You retain ownership of all content you submit to Glowlytix, including photos, notes, and log entries ("User Content"). By submitting User Content, you grant us a limited, non-exclusive license to store and process it solely to provide the Service.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            You are solely responsible for your User Content. You agree not to submit content that is illegal, harmful, or violates the rights of others.
          </p>
        </Section>

        <Section title="8. Prohibited Uses">
          <p>You agree not to:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Attempt to access other users' accounts or data.</li>
            <li style={{ marginBottom: '0.5rem' }}>Reverse-engineer, scrape, or extract data from the Service in an automated manner.</li>
            <li style={{ marginBottom: '0.5rem' }}>Use the Service to distribute malware or engage in phishing.</li>
            <li style={{ marginBottom: '0.5rem' }}>Violate any applicable law or regulation.</li>
            <li style={{ marginBottom: '0.5rem' }}>Upload photos or content depicting minors in any context.</li>
          </ul>
        </Section>

        <Section title="9. AI-Generated Content">
          <p>
            AI insights are generated by OpenAI's language model based on the data you provide. We do not guarantee the accuracy, completeness, or fitness for purpose of any AI-generated output. AI insights are provided "as is" and should not be relied upon for medical decisions.
          </p>
        </Section>

        <Section title="10. Disclaimer of Warranties">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES.
          </p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, GLOWLYTIX, INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY TO YOU SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) $50 USD.
          </p>
        </Section>

        <Section title="12. Indemnification">
          <p>
            You agree to indemnify and hold harmless Glowlytix, Inc. and its officers, directors, and employees from any claims, damages, or expenses arising out of your use of the Service, your User Content, or your violation of these Terms.
          </p>
        </Section>

        <Section title="13. Termination">
          <p>
            We may terminate or suspend your account immediately, without notice, for conduct that we determine, in our sole discretion, violates these Terms or is harmful to other users, us, or third parties. Upon termination, your right to use the Service ceases immediately. You may export your data before terminating your account.
          </p>
        </Section>

        <Section title="14. Governing Law">
          <p>
            These Terms are governed by the laws of the State of New York, USA, without regard to its conflict-of-law principles. Any dispute arising under these Terms shall be resolved in the state or federal courts located in New York County, New York.
          </p>
        </Section>

        <Section title="15. Changes to Terms">
          <p>
            We reserve the right to modify these Terms at any time. Material changes will be communicated by updating the effective date at the top and, for significant changes, by email or in-app notification. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="16. Contact">
          <p>
            Glowlytix, Inc.<br />
            Brooklyn, NY<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#7c3aed' }}>{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <div style={{ borderTop: '1px solid #e7e5e4', paddingTop: '1.5rem', marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to="/privacy" style={{ color: '#7c3aed', fontSize: '0.875rem' }}>Privacy Policy</Link>
          <Link to="/" style={{ color: '#a8a29e', fontSize: '0.875rem' }}>Back to Glowlytix</Link>
        </div>
      </div>
    </div>
  );
}
